// src/pages/Department/EditCarousel.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { message } from 'antd';
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from '../../components/Common/Breadcrumb';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import FeatherIcon from 'feather-icons-react';
import { fetchCarouselById, updateCarousel } from "../../services/carouselService";
import { fetchUserById } from "../../services/userService";
import { jwtDecode } from "jwt-decode";
import { decrypt } from '../../utils/encryption'; // 👈 Add this import
import { BASE_URL_IMAGE } from '../../config/api';

const EditCarousel = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [sequenceNumber, setSequenceNumber] = useState(0);
  const [slideTitle, setSlideTitle] = useState("");
  const [slideDescription, setSlideDescription] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [buttonPlaceholder, setButtonPlaceholder] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [image, setImage] = useState(null);
  const [currentImage, setCurrentImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [loggedInUserData, setLoggedInUserData] = useState(null);

  const fileInputRef = useRef(null);

  const breadcrumbItems = [
    { label: "Carousel" },
    { label: "List Carousel", path: "/list-carousel"},
    { label: "Edit Carousel" },
  ];

  // Fetch logged-in user
  useEffect(() => {
    const fetchLoggedInUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const user = await fetchUserById(decoded.id);
          setLoggedInUserData(user);
        } catch (err) {
          console.error("Error fetching user:", err);
          localStorage.removeItem("token");
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
    };

    fetchLoggedInUser();
  }, [navigate]);

  // Fetch carousel data
  useEffect(() => {
    const fetchCarouselData = async () => {
      setIsFetching(true);
      try {
        // Check if token exists
        const token = localStorage.getItem("token");
        if (!token) {
          message.error("Authentication token not found");
          navigate('/login');
          return;
        }

        let carouselId = id;
        
        // Check if ID is encrypted (contains = sign which is base64 padding)
        if (id && id.includes('=')) {
          try {
            carouselId = decrypt(id);
          } catch (decryptError) {
            console.error("Error decrypting ID:", decryptError);
            message.error("Invalid carousel ID");
            navigate('/list-carousel');
            return;
          }
        }

        if (!carouselId) {
          message.error("Invalid carousel ID");
          navigate('/list-carousel');
          return;
        }

        const data = await fetchCarouselById(carouselId);
        setSlideTitle(data.carousel_title || "");
        setSlideDescription(data.carousel_description || "");
        setRedirectUrl(data.carousel_url || "");
        setButtonPlaceholder(data.carousel_button_placeholder || "");
        setIsFeatured(data.carousel_isFeatured === 1);
        setSequenceNumber(data.carousel_sequence);
        setCurrentImage(data.carousel_img || "");
      } catch (err) {
        console.error("Fetch error:", err);
        message.error(err.message || "Error loading carousel data");
        navigate('/list-carousel');
      } finally {
        setIsFetching(false);
      }
    };

    fetchCarouselData();
  }, [id, navigate]);

  // Submit updated carousel
 const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    if (!slideTitle || !slideDescription || !redirectUrl || !buttonPlaceholder) {
      throw new Error("Please fill all required fields");
    }

    // Check authentication
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("Authentication required");
      navigate('/login');
      return;
    }

    let carouselId = id;
    
    // Decrypt ID if needed
    if (id && id.includes('=')) {
      try {
        carouselId = decrypt(id);
      } catch (decryptError) {
        console.error("Error decrypting ID:", decryptError);
        message.error("Invalid carousel ID");
        return;
      }
    }

    const formData = new FormData();
    formData.append("slideTitle", slideTitle);
    formData.append("slideDescription", slideDescription);
    formData.append("redirectUrl", redirectUrl);
    formData.append("buttonPlaceholder", buttonPlaceholder);
    formData.append("isFeatured", isFeatured ? 1 : 0);
    
    // ✅ Always send sequenceNumber - backend will handle the logic
    formData.append("sequenceNumber", isFeatured ? sequenceNumber : 0);

    if (image) {
      formData.append("carousel_image", image);
    }

    // ✅ Append logged-in user email
    if (loggedInUserData?.user_email) {
      formData.append("updatedbyemail", loggedInUserData.user_email);
    }

    await updateCarousel(carouselId, formData);
    message.success('Carousel updated successfully!');
    navigate('/list-carousel');

    if (image) {
      setCurrentImage(URL.createObjectURL(image));
    }

  } catch (err) {
    console.error("Submission error:", err);
    if (err.message.includes("401") || err.message.includes("Unauthorized")) {
      message.error("Session expired. Please login again.");
      localStorage.removeItem("token");
      navigate('/login');
    } else {
      message.error(err.message || "Failed to update carousel");
    }
  } finally {
    setLoading(false);
  }
};

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        message.error("Image size should be less than 2MB");
        return;
      }
      setImage(file);
      message.success('New image selected');
    }
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleRemoveImage = () => {
    setImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    message.info('New image selection cleared');
  };

  if (isFetching) {
    return (
      <div>
        <Helmet><title>Edit Carousel | CRM Jnec</title></Helmet>
        <Header />
        <Sidebar activeClassName='edit-carousel' />
        <div className="page-wrapper">
          <div className="content">
            <LoadingSpinner message="Loading carousel data..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet><title>Edit Carousel | CRM Jnec</title></Helmet>
      <Header />
      <Sidebar activeClassName='edit-carousel' />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12"><Breadcrumb items={breadcrumbItems} /></div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      {/* Form Section */}
                      <div className="col-md-8">
                        <div className="p-3 border rounded bg-white">
                          <div className="row">
                            {/* Slide title */}
                            <div className="col-12">
                              <div className="form-group local-forms">
                                <label>Slide title <span className="text-danger">*</span></label>
                                <input
                                  className="form-control"
                                  type="text"
                                  value={slideTitle}
                                  onChange={(e) => setSlideTitle(e.target.value)}
                                  maxLength={255}
                                  required
                                />
                              </div>
                            </div>

                            {/* Slide description */}
                            <div className="col-12 mt-3">
                              <div className="form-group local-forms">
                                <label>Slide Description <span className="text-danger">*</span></label>
                                <textarea
                                  className="form-control"
                                  value={slideDescription}
                                  onChange={(e) => setSlideDescription(e.target.value)}
                                  maxLength={500}
                                  rows={4}
                                  required
                                />
                              </div>
                            </div>

                            {/* Redirect URL */}
                            <div className="col-12 mt-3">
                              <div className="form-group local-forms">
                                <label>Redirect URL <span className="text-danger">*</span></label>
                                <input
                                  className="form-control"
                                  type="url"
                                  value={redirectUrl}
                                  onChange={(e) => setRedirectUrl(e.target.value)}
                                  required
                                />
                              </div>
                            </div>

                            {/* Button Placeholder */}
                            <div className="col-12 mt-3">
                              <div className="form-group local-forms">
                                <label>Button Placeholder <span className="text-danger">*</span></label>
                                <input
                                  className="form-control"
                                  type="text"
                                  value={buttonPlaceholder}
                                  onChange={(e) => setButtonPlaceholder(e.target.value)}
                                  required
                                />
                              </div>
                            </div>

                            {/* Is Featured */}
                            <div className="col-12 mt-3">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="isFeatured"
                                  checked={isFeatured}
                                  onChange={(e) => setIsFeatured(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="isFeatured">
                                  Is Featured
                                </label>
                                <small className="d-block text-muted">
                                  Enabling this will make this slide appear first in the sequence
                                </small>
                              </div>
                            </div>

                            {/* Image Section */}
                            <div className="col-12 mt-3">
                              <div className="form-group">
                                <label>Slide Image <span className="text-danger">*</span></label>

                                {/* Current Image */}
                                <div className="mb-3">
                                  <label className="d-block mb-2">Current Image:</label>
                                  <div className="border rounded p-2 d-inline-block">
                                    <img
                                      src={`${BASE_URL_IMAGE}/uploads/carouselImages/${currentImage}`}
                                      alt="Current slide"
                                      className="img-fluid"
                                      style={{ maxHeight: '150px' }}
                                    />
                                  </div>
                                </div>

                                {/* Update Image */}
                                <label className="d-block mb-2">Update Image:</label>
                                <div className="upload-box border rounded p-3 text-center">
                                  <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="d-none"
                                    accept=".png,.jpg,.jpeg,.gif,.webp"
                                    onChange={handleImageChange}
                                  />
                                  {image ? (
                                    <div className="mb-3">
                                      <div className="border rounded p-2 d-inline-block mb-2">
                                        <img
                                          src={URL.createObjectURL(image)}
                                          alt="New upload"
                                          className="img-fluid"
                                          style={{ maxHeight: '150px' }}
                                        />
                                      </div>
                                      <div className="d-flex justify-content-center gap-2">
                                        <button
                                          type="button"
                                          className="btn btn-primary btn-sm"
                                          onClick={handleBrowseClick}
                                        >
                                          Change Image
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={handleRemoveImage}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="mb-2">
                                        <i className="fas fa-cloud-upload-alt fa-3x text-muted"></i>
                                      </div>
                                      <div className="mb-2">Drag & Drop</div>
                                      <div className="text-muted small">
                                        Format: .png, .jpg, .jpeg, .gif, .webp<br />
                                        Max Size: 2MB
                                      </div>
                                      <div className="mt-2">
                                        <button
                                          type="button"
                                          className="btn btn-primary btn-sm"
                                          onClick={handleBrowseClick}
                                        >
                                          Browse Files
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="col-12 mt-4 text-end">
                              <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                              >
                                {loading ? 'Updating...' : 'Update Slide'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Instruction Section */}
                      <div className="col-md-4">
                        <div className="p-3 border rounded bg-light">
                          <h5 className="mb-3">Instructions</h5>
                          <ul className="list-unstyled small">
                            <li className="mb-2">• Keep slide titles concise and descriptive.</li>
                            <li className="mb-2">• Descriptions should be engaging but brief.</li>
                            <li className="mb-2">• Use high-quality images for best results.</li>
                            <li className="mb-2">• Recommended image dimensions: 1920x1080px.</li>
                            <li className="mb-2">• Max Image Size: 2MB</li>
                            <li className="mb-2">• Featured slides will appear first in the carousel.</li>
                            <li className="mb-2">• Button placeholder text should be action-oriented.</li>
                            <li className="mb-2">• Recommended text: "Apply Now", "Know More", etc.</li>
                            <li className="mb-2">• Compress images using:
                              <ul>
                                <li><a href="https://tinypng.com/" target="_blank" rel="noopener noreferrer"><FeatherIcon icon="external-link" size={14} className="ms-1"/> TinyPNG</a></li>
                                <li><a href="https://compressor.io/" target="_blank" rel="noopener noreferrer"><FeatherIcon icon="external-link" size={14} className="ms-1"/> Compressor.io</a></li>
                                <li><a href="https://squoosh.app/" target="_blank" rel="noopener noreferrer"><FeatherIcon icon="external-link" size={14} className="ms-1"/> Squoosh</a></li>
                              </ul>
                            </li>
                          </ul>
                        </div>
                      </div>

                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EditCarousel;